import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Tag, Copy, Check, Clock, AlertCircle, ShoppingBag } from 'lucide-react';
import { accountApi } from '../../api/account';
import type { CustomerCouponDto } from '../../api/types';
import { Seo } from '../../components/Seo';
import { PageLoader } from '../../components/ui';
import { money, formatDate } from '../../lib/format';

export function CouponsPage() {
  const [activeTab, setActiveTab] = useState<'Active' | 'Used' | 'Expired'>('Active');
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const { data: coupons = [], isLoading, error } = useQuery<CustomerCouponDto[]>({
    queryKey: ['account', 'coupons'],
    queryFn: () => accountApi.coupons(),
  });

  const handleCopy = (code: string) => {
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    }
  };

  const activeCoupons = coupons.filter((c) => c.status === 'Active');
  const usedCoupons = coupons.filter((c) => c.status === 'Used');
  const expiredCoupons = coupons.filter((c) => c.status === 'Expired');

  const currentList =
    activeTab === 'Active'
      ? activeCoupons
      : activeTab === 'Used'
        ? usedCoupons
        : expiredCoupons;

  if (isLoading) return <PageLoader label="Kuponlarınız yükleniyor…" />;

  return (
    <div className="space-y-6">
      <Seo title="Kuponlarım — Hesabım" noIndex />

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b border-ink-100 pb-4">
        <div>
          <h2 className="text-xl font-bold text-ink-900 flex items-center gap-2">
            <Tag className="h-6 w-6 text-brand-600" />
            Kuponlarım ve İndirimlerim
          </h2>
          <p className="text-sm text-ink-500 mt-1">
            Hesabınıza tanımlanan özel indirim kodları ve kampanyaları buradan takip edebilirsiniz.
          </p>
        </div>
      </div>

      {/* Sekmeler */}
      <div className="flex gap-2 border-b border-ink-200">
        <button
          type="button"
          onClick={() => setActiveTab('Active')}
          className={`pb-3 px-3 text-sm font-semibold transition border-b-2 flex items-center gap-1.5 ${
            activeTab === 'Active'
              ? 'border-brand-600 text-brand-700'
              : 'border-transparent text-ink-500 hover:text-ink-900'
          }`}
        >
          Aktif Kuponlar
          <span
            className={`rounded-full px-2 py-0.5 text-xs ${
              activeTab === 'Active'
                ? 'bg-brand-100 text-brand-800'
                : 'bg-ink-100 text-ink-600'
            }`}
          >
            {activeCoupons.length}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('Used')}
          className={`pb-3 px-3 text-sm font-semibold transition border-b-2 flex items-center gap-1.5 ${
            activeTab === 'Used'
              ? 'border-brand-600 text-brand-700'
              : 'border-transparent text-ink-500 hover:text-ink-900'
          }`}
        >
          Kullanılmış Kuponlar
          <span className="rounded-full bg-ink-100 px-2 py-0.5 text-xs text-ink-600">
            {usedCoupons.length}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('Expired')}
          className={`pb-3 px-3 text-sm font-semibold transition border-b-2 flex items-center gap-1.5 ${
            activeTab === 'Expired'
              ? 'border-brand-600 text-brand-700'
              : 'border-transparent text-ink-500 hover:text-ink-900'
          }`}
        >
          Süresi Dolanlar
          <span className="rounded-full bg-ink-100 px-2 py-0.5 text-xs text-ink-600">
            {expiredCoupons.length}
          </span>
        </button>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 flex items-center gap-2">
          <AlertCircle className="h-5 w-5 shrink-0" />
          Kuponlar yüklenirken bir sorun oluştu. Lütfen sayfayı yenileyiniz.
        </div>
      ) : currentList.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-ink-200 bg-ink-50/50 p-12 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-brand-50 text-brand-600 mb-4">
            <Tag className="h-7 w-7" />
          </div>
          <h3 className="text-base font-semibold text-ink-900">
            {activeTab === 'Active'
              ? 'Henüz aktif bir kuponunuz bulunmuyor'
              : activeTab === 'Used'
                ? 'Daha önce kullanılmış kuponunuz bulunmuyor'
                : 'Süresi dolmuş kuponunuz bulunmuyor'}
          </h3>
          <p className="mt-1 text-sm text-ink-500 max-w-sm mx-auto">
            {activeTab === 'Active'
              ? 'Kampanyalarımızı takip ederek ve e-bültenimize katılarak yeni indirim kuponları kazanabilirsiniz.'
              : 'Siparişlerinizde kupon kullandığınızda geçmiş kayıtları burada görebilirsiniz.'}
          </p>
          {activeTab === 'Active' && (
            <Link to="/" className="btn-primary mt-5 inline-flex items-center gap-2">
              <ShoppingBag className="h-4 w-4" />
              Alışverişe Başla
            </Link>
          )}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {currentList.map((coupon) => {
            const isInactive = coupon.status !== 'Active';

            return (
              <div
                key={coupon.id}
                className={`relative flex flex-col justify-between overflow-hidden rounded-2xl border transition-all shadow-sm ${
                  isInactive
                    ? 'border-ink-200 bg-ink-50/40 opacity-75'
                    : 'border-brand-200 bg-white hover:shadow-md'
                }`}
              >
                {/* Sol Kupon Kuşağı */}
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <span
                        className={`inline-block rounded-md px-2.5 py-1 text-xs font-bold tracking-wide uppercase ${
                          coupon.status === 'Active'
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : coupon.status === 'Used'
                              ? 'bg-ink-100 text-ink-600 border border-ink-200'
                              : 'bg-rose-50 text-rose-700 border border-rose-200'
                        }`}
                      >
                        {coupon.status === 'Active'
                          ? 'Kullanılabilir'
                          : coupon.status === 'Used'
                            ? 'Kullanıldı'
                            : 'Süresi Doldu'}
                      </span>

                      <h4 className="mt-2 text-lg font-bold text-ink-900">
                        {coupon.type === 'Percentage'
                          ? `%${coupon.value} İndirim`
                          : coupon.type === 'FreeShipping'
                            ? 'Ücretsiz Kargo'
                            : `${money(coupon.value)} İndirim`}
                      </h4>
                      <p className="text-xs font-medium text-ink-700 mt-0.5">{coupon.title}</p>
                    </div>

                    <div className="text-right">
                      {coupon.type === 'Percentage' && coupon.maximumDiscountAmount ? (
                        <span className="block text-[11px] text-ink-400">
                          Maks. {money(coupon.maximumDiscountAmount)}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  {coupon.description && (
                    <p className="mt-2 text-xs text-ink-600 line-clamp-2">
                      {coupon.description}
                    </p>
                  )}

                  {/* Kısıtlamalar ve Bilgiler */}
                  <div className="mt-4 space-y-1.5 border-t border-ink-100 pt-3 text-xs text-ink-600">
                    <div className="flex items-center gap-1.5">
                      <span className="font-semibold text-ink-700">Sepet Limiti:</span>
                      {coupon.minimumSubtotal && coupon.minimumSubtotal > 0 ? (
                        <span className="text-brand-700 font-medium">
                          {money(coupon.minimumSubtotal)} ve üzeri siparişlerde
                        </span>
                      ) : (
                        <span className="text-emerald-600">Alt sepet limiti yok</span>
                      )}
                    </div>

                    {coupon.entitledCategoryNames && coupon.entitledCategoryNames.length > 0 && (
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-ink-700">Geçerli Kategori:</span>
                        <span className="text-ink-800 font-medium truncate">
                          {coupon.entitledCategoryNames.join(', ')}
                        </span>
                      </div>
                    )}

                    {coupon.endsAt && (
                      <div className="flex items-center gap-1.5 text-ink-500">
                        <Clock className="h-3.5 w-3.5" />
                        <span>Son geçerlilik: {formatDate(coupon.endsAt)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Alt Kod Alanı ve Aksiyon */}
                <div
                  className={`flex items-center justify-between border-t px-5 py-3 ${
                    isInactive
                      ? 'border-ink-200 bg-ink-100/50'
                      : 'border-brand-100 bg-brand-50/40'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-ink-500">Kupon Kodu:</span>
                    <span className="font-mono text-sm font-bold tracking-wider text-ink-900 bg-white px-2.5 py-0.5 rounded border border-ink-200">
                      {coupon.code}
                    </span>
                  </div>

                  {coupon.status === 'Active' ? (
                    <button
                      type="button"
                      onClick={() => handleCopy(coupon.code)}
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-700 hover:text-brand-900 transition"
                      title="Kodu kopyala"
                    >
                      {copiedCode === coupon.code ? (
                        <>
                          <Check className="h-4 w-4 text-emerald-600" />
                          <span className="text-emerald-600">Kopyalandı!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4" />
                          <span>Kopyala</span>
                        </>
                      )}
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
